import React, { useEffect, useState } from "react";
import api from "../services/api";

const ServiceAreaManagement = () => {
  const [area, setArea] = useState({ pickupAllowedCities: [], dropAllowedDistricts: [] });
  const [loading, setLoading] = useState(true);
  const [newPickupCity, setNewPickupCity] = useState("");
  const [newDropDistrict, setNewDropDistrict] = useState("");
  const [addingPickup, setAddingPickup] = useState(false);
  const [addingDrop, setAddingDrop] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const fetchArea = async () => {
    try {
      setLoading(true);
      const res = await api.get("/service-area");
      if (res.data.success) setArea(res.data.area);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load service area settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchArea();
  }, []);

  const handleAddPickup = async (e) => {
    e.preventDefault();
    if (!newPickupCity.trim()) return;

    try {
      setAddingPickup(true);
      setMessage("");
      setError("");
      const res = await api.post("/service-area/pickup", { cityName: newPickupCity.trim() });
      setMessage(res.data.message);
      setNewPickupCity("");
      fetchArea();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to verify/add city");
    } finally {
      setAddingPickup(false);
    }
  };

  const handleAddDrop = async (e) => {
    e.preventDefault();
    if (!newDropDistrict.trim()) return;

    try {
      setAddingDrop(true);
      setMessage("");
      setError("");
      const res = await api.post("/service-area/drop", { districtName: newDropDistrict.trim() });
      setMessage(res.data.message);
      setNewDropDistrict("");
      fetchArea();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to verify/add district");
    } finally {
      setAddingDrop(false);
    }
  };

  const handleRemove = async (listType, value) => {
    if (!window.confirm(`Remove "${value}" from ${listType} areas?`)) return;
    try {
      await api.post("/service-area/remove", { listType, value });
      fetchArea();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to remove");
    }
  };

  if (loading) {
    return <div style={styles.loading}>Loading Service Area Settings...</div>;
  }

  return (
    <div style={styles.page}>
      <h2 style={styles.title}>Service Area Management</h2>
      <p style={styles.subtitle}>
        Control which cities allow pickup and which districts allow drop-off. New entries are automatically verified against real locations before being added.
      </p>

      {message && <div style={styles.successBanner}>{message}</div>}
      {error && <div style={styles.errorBanner}>{error}</div>}

      <div style={styles.grid}>
        {/* PICKUP CITIES */}
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>Pickup Allowed Cities</h3>
          <p style={styles.helpText}>Customers can only select a pickup location within these cities.</p>

          <form onSubmit={handleAddPickup} style={styles.addForm}>
            <input
              style={styles.input}
              type="text"
              value={newPickupCity}
              onChange={(e) => setNewPickupCity(e.target.value)}
              placeholder="e.g. Indapur"
            />
            <button style={styles.addBtn} type="submit" disabled={addingPickup}>
              {addingPickup ? "Verifying..." : "Add"}
            </button>
          </form>

          <div style={styles.chipList}>
            {area.pickupAllowedCities.map((city) => (
              <div key={city} style={styles.chip}>
                {city}
                <button style={styles.chipRemove} onClick={() => handleRemove("pickup", city)}>
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* DROP DISTRICTS */}
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>Drop Allowed Districts</h3>
          <p style={styles.helpText}>Customers can only select a drop location within these districts.</p>

          <form onSubmit={handleAddDrop} style={styles.addForm}>
            <input
              style={styles.input}
              type="text"
              value={newDropDistrict}
              onChange={(e) => setNewDropDistrict(e.target.value)}
              placeholder="e.g. Solapur"
            />
            <button style={styles.addBtn} type="submit" disabled={addingDrop}>
              {addingDrop ? "Verifying..." : "Add"}
            </button>
          </form>

          <div style={styles.chipList}>
            {area.dropAllowedDistricts.map((district) => (
              <div key={district} style={styles.chip}>
                {district}
                <button style={styles.chipRemove} onClick={() => handleRemove("drop", district)}>
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  page: { padding: "30px", background: "#f8fafc", minHeight: "100vh", fontFamily: "'Inter', sans-serif" },
  title: { margin: 0, fontSize: "28px", color: "#1e293b", fontWeight: "700" },
  subtitle: { margin: "5px 0 24px 0", color: "#64748b", fontSize: "14px", maxWidth: "600px" },
  successBanner: { background: "#dcfce7", color: "#166534", padding: "12px 16px", borderRadius: "8px", marginBottom: "16px", fontSize: "14px", fontWeight: "600" },
  errorBanner: { background: "#fee2e2", color: "#991b1b", padding: "12px 16px", borderRadius: "8px", marginBottom: "16px", fontSize: "14px", fontWeight: "600" },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: "20px" },
  card: { background: "#fff", borderRadius: "16px", padding: "24px", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)" },
  cardTitle: { margin: "0 0 6px 0", fontSize: "16px", color: "#1e293b" },
  helpText: { fontSize: "12px", color: "#94a3b8", marginBottom: "16px" },
  addForm: { display: "flex", gap: "8px", marginBottom: "16px" },
  input: { flex: 1, padding: "10px 12px", borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "14px" },
  addBtn: { padding: "10px 18px", borderRadius: "8px", border: "none", background: "#2563eb", color: "#fff", fontWeight: "600", cursor: "pointer" },
  chipList: { display: "flex", flexWrap: "wrap", gap: "8px" },
  chip: { display: "flex", alignItems: "center", gap: "8px", background: "#eef2ff", color: "#4338ca", padding: "6px 10px 6px 14px", borderRadius: "20px", fontSize: "13px", fontWeight: "600", textTransform: "capitalize" },
  chipRemove: { background: "none", border: "none", color: "#4338ca", fontSize: "16px", fontWeight: "800", cursor: "pointer", padding: "0 4px" },
  loading: { textAlign: "center", padding: "100px", fontSize: "20px" },
};

export default ServiceAreaManagement;